import { useMutation, useQuery } from '@apollo/client';
import {
  CREATE_SCORECARD,
  GET_SCORECARD,
  ScorecardCreateVariables,
  ScorecardCriteria,
  ScorecardUpdateVariables,
  UPDATE_CRITERIA_STATUS,
  UPDATE_SCORECARD,
  UpdateCriteriaStatusVariables,
} from './api';
import { logError } from 'utils/logger';
import { useCallback, useState } from 'react';
import { useNotifications } from 'modules/notification';

export function useScorecardBuilder() {
  const [scorecardCriteria, setScorecardCriteria] = useState<ScorecardCriteria | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const { showNotification } = useNotifications();

  const { data: scorecardData, refetch: refetchScorecard } = useQuery(GET_SCORECARD, {
    notifyOnNetworkStatusChange: true,
    onCompleted(data) {
      setScorecardCriteria(data.scoreCard[0]?.prescreenCriteria);
      setIsLoading(false);
    },
  });

  const [onCreateScorecard, { loading: isCreating }] = useMutation(CREATE_SCORECARD);

  const [onUpdateScorecard, { loading: isUpdating }] = useMutation(UPDATE_SCORECARD);

  const [onUpdateCriteriaStatus, { loading: isCriteriaStatusUpdating }] = useMutation(UPDATE_CRITERIA_STATUS);

  const handleCreateScorecard = useCallback(
    async (createDTO: ScorecardCreateVariables['input']) => {
      try {
        setIsLoading(true);
        await onCreateScorecard({ variables: { input: createDTO } });
        await refetchScorecard();
        showNotification('Scorecard Created');
      } catch (error) {
        showNotification('Error', { type: 'error' });
        logError(error);
      }
    },
    [onCreateScorecard, showNotification]
  );

  const handleUpdateScorecard = useCallback(
    async (updateDTO: ScorecardUpdateVariables['input']) => {
      try {
        setIsLoading(true);
        await onUpdateScorecard({ variables: { input: updateDTO } });
        await refetchScorecard();
        showNotification('Updated!');
      } catch (error) {
        showNotification('Error', { type: 'error' });
        logError(error);
      }
    },
    [onUpdateScorecard, refetchScorecard, showNotification]
  );

  const handleUpdateCriteriaStatus = useCallback(
    async (updateDTO: UpdateCriteriaStatusVariables['input']) => {
      try {
        setIsLoading(true);
        await onUpdateCriteriaStatus({ variables: { input: updateDTO } });
        await refetchScorecard();
        showNotification('Status  updated!');
      } catch (error) {
        showNotification('Error', { type: 'error' });
        logError(error);
      }
    },
    [onUpdateCriteriaStatus, refetchScorecard, showNotification]
  );

  return {
    financeProgramId: scorecardData?.scoreCard[0]?.financeProgramId,
    scorecardCriteria,
    isLoading,
    handleCreateScorecard,
    handleUpdateScorecard,
    handleUpdateCriteriaStatus,
  };
}
